import { axios } from '@boot/axios.js';

const ASYNC_STATUS_FREQUENCY_MS = parseInt(
    process.env.ASYNC_STATUS_FREQUENCY_MS || 1000
);

// Explicitly set timeout for the async task API calls
// For tasks that require more time to complete (e.g. large reports),
// the server might take longer to respond with the initial task status request than the default axios timeout
// This can cause frontend to cancel the async task request and show an error to the user
// TODO: Once we are able to optimize the API, we can lessen/remove this custom timeout value
const ASYNC_TASK_REQUEST_TIMEOUT_MS = parseInt(
    process.env.ASYNC_TASK_REQUEST_TIMEOUT_MS || 120000 // 2 minutes
);

export default class AsyncManager {
    constructor({ useLegacyTaskStatusEndpoint = false }) {
        this.intervalId = null;
        this.taskId = null;
        this.lastUpdated = performance.now();
        this.waitingForUpdate = false;
        // legacy task status endpoint
        this.useLegacyTaskStatusEndpoint = useLegacyTaskStatusEndpoint;
        if (useLegacyTaskStatusEndpoint) {
            this.taskStatusEndpoint = 'task_status';
        } else {
            this.taskStatusEndpoint = 'tasks';
        }
    }

    async asyncLoad(endpoint, payload = {}) {
        return new Promise((resolve, reject) => {
            axios
                .post(endpoint, payload, {
                    timeout: ASYNC_TASK_REQUEST_TIMEOUT_MS
                })
                .then(response => {
                    if (response.status == 202) {
                        resolve(
                            this.startPeriodicStatusCheck(response.data.task_id)
                        );
                    } else {
                        reject({
                            error: 'Task was not accepted'
                        });
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    /**
     * Set interval to get the status of the celery task
     * @param (String) taskId - task id
     */
    async startPeriodicStatusCheck(taskId) {
        this.taskId = taskId;
        // set the interval function
        return new Promise((resolve, reject) => {
            // if taskId is null, reject
            if (!this.taskId) {
                this.stopPeriodicStatusCheck();
                reject({ error: 'Task id is invalid' });
            }
            this.intervalId = setInterval(async () => {
                if (
                    performance.now() - this.lastUpdated >=
                        ASYNC_STATUS_FREQUENCY_MS &&
                    this.waitingForUpdate === false
                ) {
                    // set async waiting for update to true
                    this.waitingForUpdate = true;

                    try {
                        // get the task status
                        const response = await axios.get(
                            `${this.taskStatusEndpoint}/${this.taskId}/`,
                            {
                                timeout: ASYNC_TASK_REQUEST_TIMEOUT_MS
                            }
                        );
                        if (response.status === 200) {
                            // get the status of the report task
                            const taskStatus = response.data.status;
                            // check response task_id matches currently loading task ID
                            // (race condition for the cancel button)
                            if (response.data.id === this.taskId) {
                                switch (taskStatus) {
                                    case 'SUCCESS':
                                        // stop the period status check
                                        this.stopPeriodicStatusCheck();
                                        // resolve the result
                                        resolve(response.data.result);
                                        break;
                                    case 'FAILURE':
                                        this.stopPeriodicStatusCheck();
                                        reject({
                                            error: 'Task failed'
                                        });
                                        break;
                                }
                            }
                        } else {
                            this.cancelAsyncLoading();
                            reject({
                                error: 'Task status endpoint returned wrong HTTP code'
                            });
                        }
                    } catch {
                        this.cancelAsyncLoading();
                        reject({
                            error: 'Error during request to task status endpoint'
                        });
                    }

                    // reset the async loading variables
                    this.lastUpdated = performance.now();
                    this.waitingForUpdate = false;
                }
            }, 100);
        });
    }

    /**
     * Stop all activites (frontend and backend) related to
     * the async report generation
     */
    cancelAsyncLoading() {
        // save the current taskId before calling `stopPeriodicStatusCheck`
        // as this will clear the task id stored in the state
        const taskId = this.taskId;

        if (taskId) {
            try {
                if (!this.useLegacyTaskStatusEndpoint) {
                    this.cancelCeleryTask(taskId);
                }
            } catch (error) {
                console.error(error);
            }
        }
        // when using the legacy task status endpoint - task cant be cancelled

        this.stopPeriodicStatusCheck();
    }

    /**
     * Stops the periodic status check for asynchronous report generation
     */
    stopPeriodicStatusCheck() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.resetAsyncVariables();
    }

    /**
     * Reset the async variables
     */
    resetAsyncVariables() {
        this.intervalId = null;
        this.taskId = null;
        this.lastUpdated = performance.now();
        this.waitingForUpdate = false;
    }

    /**
     * Send an API request to cancel the asynchronous report generation task
     * @param (String) taskId - task id
     */
    cancelCeleryTask(taskId) {
        if (taskId && !this.useLegacyTaskStatusEndpoint) {
            try {
                // if cancelling fails, we do not retry or else this may
                // cause a delay in the flow of the application. It is enough
                // to simply make the request, 99% of the time it will succeed
                axios.post(`tasks/${taskId}/cancel/`);
            } catch (error) {
                console.error(error);
            }
        }
    }
}
